import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { AnimatedSection } from "@/components/shared/AnimatedSection";
import { asset } from "@/lib/assets";
const problemInconsistencies = asset("case-studies/inconsistencies/problem-inconsistencies.png");
const componentsPrioritization = asset("case-studies/inconsistencies/components-priotitization.png");
const figmaLibrary = asset("case-studies/inconsistencies/figmalibrary.png");
const firstDS = asset("case-studies/inconsistencies/firstds.png");
const dsHub = asset("case-studies/inconsistencies/DS-hub.mp4");
const agentPortal = asset("case-studies/inconsistencies/agentportal.png");
const lenderPortal = asset("case-studies/inconsistencies/portal.png");
const lenderPortal2 = asset("case-studies/inconsistencies/lenderportal.png");
const borrowerExperience = asset("case-studies/inconsistencies/borrowerexperience.png");

const META = [
  { label: "Timeline", value: "4-day hackathon + 3 weeks" },
  { label: "My role", value: "Lead Designer + Builder" },
  { label: "Team", value: "1 Designer · 1 Engineer" },
  { label: "Stack", value: "Shadcn · Tailwind · Figma MCP · Cursor" },
];

function ImageSlot({
  src,
  alt,
  className = "",
  caption,
}: {
  src: string;
  alt: string;
  className?: string;
  caption?: string;
}) {
  const [lightbox, setLightbox] = useState(false);

  return (
    <>
      <div className="w-full">
        <div
          className={`bg-[#EEF2FF] border border-[#E2E8F0] rounded-2xl overflow-hidden flex items-center justify-center cursor-zoom-in ${className}`}
          onClick={() => setLightbox(true)}
          title="Click to expand"
        >
          <img
            src={src}
            alt={alt}
            className="w-full h-full object-cover"
            onError={(e) => {
              const el = e.currentTarget as HTMLImageElement;
              el.style.display = "none";
              const parent = el.parentElement;
              if (parent && !parent.querySelector(".placeholder-label")) {
                const label = document.createElement("p");
                label.className =
                  "placeholder-label text-xs text-[#888888] text-center p-4";
                label.textContent = alt;
                parent.appendChild(label);
              }
            }}
          />
        </div>
        {caption && (
          <p className="mt-2 text-xs text-[#888888] font-[DM_Sans] text-center">
            {caption}
          </p>
        )}
      </div>

      {lightbox &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setLightbox(false)}
          >
            <img
              src={src}
              alt={alt}
              className="max-w-full max-h-full rounded-xl shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setLightbox(false)}
              className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none"
              aria-label="Close"
            >
              ×
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

function VideoSlot({
  src,
  className = "",
}: {
  src: string;
  className?: string;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.3 },
    );
    observer.observe(video);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`rounded-2xl overflow-hidden bg-[#1A1A1A] ${className}`}>
      <video
        ref={videoRef}
        src={src}
        loop
        muted
        playsInline
        controls
        className="w-full h-full object-cover"
      />
    </div>
  );
}

const NAV_SECTIONS = [
  { id: "problem", label: "The problem" },
  { id: "how-it-happened", label: "How it happened" },
  { id: "in-production", label: "In production" },
  { id: "learnings", label: "Learnings" },
];

function SideNav() {
  const [active, setActive] = useState<string>(NAV_SECTIONS[0].id);

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    const visibleSections = new Map<string, number>();

    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            visibleSections.set(id, entry.intersectionRatio);
          } else {
            visibleSections.delete(id);
          }
          if (visibleSections.size > 0) {
            const topmost = [...visibleSections.entries()].reduce((a, b) =>
              a[1] >= b[1] ? a : b,
            );
            setActive(topmost[0]);
          }
        },
        { threshold: [0, 0.25, 0.5], rootMargin: "-10% 0px -60% 0px" },
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  return (
    <nav className="hidden xl:flex flex-col gap-1 sticky top-32 self-start w-48 shrink-0">
      {NAV_SECTIONS.map(({ id, label }) => (
        <a
          key={id}
          href={`#${id}`}
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById(id)
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className={`text-xs font-medium py-1.5 pl-3 border-l-2 transition-all duration-200 ${
            active === id
              ? "border-[#1D3FE8] text-[#1A1A1A]"
              : "border-transparent text-[#888888] hover:text-[#1A1A1A] hover:border-[#E2E8F0]"
          }`}
        >
          {label}
        </a>
      ))}
    </nav>
  );
}

export function CaseStudyDesignSystem() {
  return (
    <div className="min-h-screen bg-[#F7F8FA] font-[DM_Sans]">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-[#F7F8FA]/90 backdrop-blur-sm border-b border-[#E2E8F0]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-4 flex items-center justify-between">
          <Link
            to="/#work"
            className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
          >
            ← All work
          </Link>
          <Link
            to="/"
            className="font-[Bricolage_Grotesque] font-bold text-sm text-[#1A1A1A]"
          >
            Verónica <span className="text-[#1D3FE8]">Aguilar</span>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <div className="max-w-5xl mx-auto px-6 sm:px-12 pt-24 pb-20">
        <AnimatedSection>
          <h1 className="font-[Bricolage_Grotesque] font-extrabold tracking-[-0.04em] text-[clamp(2.25rem,5vw,3.75rem)] text-[#1A1A1A] leading-[1.05] mb-6">
            One source of truth for{" "}
            <span className="text-[#1D3FE8]">design and code.</span>
          </h1>
          <p className="text-[#555] text-lg leading-[1.8] max-w-[680px] mb-12">
            Getting design and code to match was a constant negotiation.
            Engineers rebuilding from scratch, designers pushing for
            consistency. We decided to fix the root cause instead of the
            symptoms.
          </p>

          {/* Meta strip */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-[#E2E8F0]">
            {META.map((m) => (
              <div key={m.label}>
                <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                  {m.label}
                </p>
                <p className="text-[0.9375rem] font-medium text-[#1A1A1A]">
                  {m.value}
                </p>
              </div>
            ))}
          </div>
        </AnimatedSection>
      </div>

      {/* Metrics banner */}
      <AnimatedSection>
        <div className="bg-[#1A1A1A] px-12 py-12 grid sm:grid-cols-2 gap-0">
          <div className="sm:border-r border-white/[0.08] sm:pr-10 sm:text-center">
            <p className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(2.5rem,5vw,3.5rem)] tracking-[-0.04em] leading-none text-[#F5C542] mb-2">
              70%
            </p>
            <p className="text-white/50 text-sm leading-relaxed">
              Drop in frontend development time
              <br />
              <span className="text-xs opacity-50">
                Reported by Director of Product and CTO
              </span>
            </p>
          </div>
          <div className="sm:pl-10 mt-8 sm:mt-0 sm:text-center">
            <p className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(2.5rem,5vw,3.5rem)] tracking-[-0.04em] leading-none text-[#F5C542] mb-2">
              4+
            </p>
            <p className="text-white/50 text-sm leading-relaxed">
              Products using the system
              <br />
              in production today
            </p>
          </div>
        </div>
      </AnimatedSection>

      {/* Sections */}
      <div className="max-w-6xl mx-auto px-6 sm:px-12">
        <div className="flex gap-16 items-start">
          <SideNav />
          <div className="flex-1 min-w-0">
            {/* Section 1: The problem */}
            <AnimatedSection
              id="problem"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                The problem
              </p>
              <div>
                <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-5">
                  We had a design system. We just had{" "}
                  <span className="text-[#1D3FE8]">n</span> versions of it.
                </h2>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[640px] mb-10">
                  As HomeLight grew and new repos were created for each product,
                  engineers kept rebuilding the same UI components from scratch,
                  each time with slight variations. The design system lived in
                  Figma but was never implemented in code, so hover states,
                  border radius, and hex values differed across products.
                  Inconsistencies were caught in bug bashes and quietly
                  deprioritized. On top of that, as PMs and ops started building
                  prototypes with AI, they reached for default component
                  libraries instead of ours, and the brand drifted further.
                </p>
                <ImageSlot
                  src={problemInconsistencies}
                  alt="Component inconsistencies across products"
                  className="w-full aspect-[16/7]"
                  caption="Example of QA notes with UI inconsistencies"
                />
              </div>
            </AnimatedSection>

            {/* Section 2: How it happened */}
            <AnimatedSection
              id="how-it-happened"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                How it happened
              </p>

              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                4 days to prove the concept.
              </h2>

              <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-12">
                This started as a hackathon. We had 4 days to go from nothing to
                a working system.
              </p>

              <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-10">
                What we did.
              </p>

              {/* Step 1 */}
              <div className="mb-12">
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  1. Definition
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px]">
                  Along with engineering we decided to use an existing component
                  library. We ended up going with Shadcn because of its
                  long-term flexibility and control, Tailwind for CSS and Lucid
                  icon library. At first, Tailgrid seemed great for the number
                  of components and layouts, but it is harder to customize.
                  Shadcn might have fewer components, but we could fully
                  customize them and adapt for our different portals.
                </p>
              </div>

              {/* Step 2 */}
              <div className="mb-8">
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  2. Audit
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-8">
                  Since we started at a hackathon I gave priority to the
                  components we needed on one flow, the Buy Before You Sell loan
                  application. Since I was familiar with our 4 products I
                  quickly identified which components won't be needed and
                  removed them from the library, like; command bar, charts,etc.
                  So, we started with 10 components needed to test in that flow.
                </p>
                <ImageSlot
                  src={componentsPrioritization}
                  alt="Components prioritization"
                  className="w-full aspect-[16/7]"
                />
              </div>

              {/* Step 3 */}
              <div className="mb-8 mt-12">
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  3. Figma libraries
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-8">
                  I was lucky that the hackathon was the first week of the
                  designer I hired. So we took this opportunity to show him the
                  most important flows and divided the component list of our new
                  library. We tested each other's components to make sure it had
                  the variables connected and enough properties to easily edit a
                  component. The big question was (without knowing what we know
                  now) How do we send our components to code.
                </p>
                <ImageSlot
                  src={figmaLibrary}
                  alt="Figma library"
                  className="w-full aspect-[16/7]"
                />
              </div>

              {/* Step 4 */}
              <div className="mt-12">
                <p className="text-[#1A1A1A] text-[0.9375rem] font-semibold mb-3">
                  4. From Figma to code
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-8">
                  Engineering was already coding our first components in the
                  hackathon for our test project. But we wanted to go beyond so
                  we tried Figma MCP to migrate the components to our design
                  repo and I vibecoded a prototype of our component library for
                  the company to present it to the team.
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-8">
                  Scrappy, but{" "}
                  <strong className="text-[#1A1A1A]">
                    better done than perfect.
                  </strong>{" "}
                  A few years ago this would be impossible or so long that the
                  company won't approved it. So, I take the win.
                </p>
                <ImageSlot
                  src={firstDS}
                  alt="First design system prototype"
                  className="w-full aspect-[16/7]"
                />
              </div>

              {/* Outcome */}
              <div className="mt-16 pt-12 border-t border-[#E2E8F0]">
                <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-6">
                  One library. Every team builds from it.
                </h2>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-4">
                  The concept proved successful among the EPD team so in the
                  following weeks we continue polishing our Figma library and
                  Design System hub and realise it company-wide.
                </p>
                <p className="text-[#444] text-[0.9375rem] leading-[1.85] max-w-[680px] mb-10">
                  We currently use the Design system for all of our products,
                  it's hard to integrate to legacy repos, but we have managed to
                  add it to the new ones. Some prefer the hub, some prefer to
                  connect figma to their projects or copy links directly. It has
                  been a lesson how technology is evolving and acknowledge that
                  our solutions are not written in stone, we are up for the
                  challenge to continue learning and adapting.
                </p>
                <VideoSlot src={dsHub} className="w-full aspect-video" />
              </div>
            </AnimatedSection>

            {/* Section 3: In production */}
            <AnimatedSection
              id="in-production"
              className="py-20 border-b border-[#E2E8F0]"
            >
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                In production
              </p>
              {/* The shift callout */}
              <div className="bg-[#1A1A1A] rounded-2xl p-8 relative overflow-hidden mb-8">
                <div className="absolute top-[-60px] right-[-60px] w-48 h-48 bg-[#1D3FE8] rounded-full opacity-10" />
                <p className="text-white/80 text-[1.0625rem] leading-[1.8] max-w-[640px] relative z-10">
                  New products being onboarded now get the library installed at
                  setup. As HomeLight migrates repos to newer environments, the
                  DS goes in from day one.{" "}
                  <strong className="text-white font-semibold">
                    No more retrofitting. No more drift.
                  </strong>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Agent portal", src: agentPortal },
                  { label: "Lender portal", src: lenderPortal },
                  { label: "Lender portal", src: lenderPortal2 },
                  { label: "Client experience", src: borrowerExperience },
                ].map((item) => (
                  <div key={item.label}>
                    <ImageSlot
                      src={item.src}
                      alt={item.label}
                      className="aspect-video mb-2"
                    />
                    <p className="text-xs text-[#888888] font-medium">
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            {/* Section 4: Learnings */}
            <AnimatedSection id="learnings" className="py-20">
              <p className="text-xs font-medium uppercase tracking-[0.08em] text-[#1D3FE8] mb-4 flex items-center gap-2">
                <span className="w-5 h-0.5 bg-[#1D3FE8] inline-block" />
                Learnings
              </p>
              <h2 className="font-[Bricolage_Grotesque] font-extrabold text-[clamp(1.5rem,3vw,2.25rem)] tracking-[-0.03em] text-[#1A1A1A] leading-[1.15] mb-10">
                What this project taught me.
              </h2>

              <div className="flex flex-col gap-6">
                {[
                  {
                    label: "None of this happens without engineering help",
                    paragraphs: [
                      "This was one of my first AI-powered projects and the setup itself was a big learning curve. PRs, commits, Cursor, local environments. My teammates were very patient and generous while I figured things out. Being in the same office for a week helped a lot.",
                    ],
                  },
                  {
                    label: "Getting the component right",
                    paragraphs: [
                      "Components did not migrate cleanly the first time. At the beginning we used only Figma MCP, which resulted in some broken styles. As we learned more about the process and looked at how other companies were doing it, we realized that Code Connect was what we needed to ensure better parity between Figma and code (still needs regiment but it's better).",
                      "Eventually we found that Code Connect was what we needed to ensure better parity between Figma and code.",
                    ],
                  },
                  {
                    label: "Designers become a bridge, not a bottleneck",
                    paragraphs: [
                      "One of the best parts of this project was seeing people outside EPD open V0, Cursor, etc and reference the design system to prototype their own ideas. When stakeholders can quickly show what they mean, conversations change. Those prototypes became a starting point instead of long alignment meetings. That is the direction I want to keep pushing.",
                    ],
                  },
                ].map((item, i) => (
                  <div key={i} className="flex gap-6 items-start">
                    <span className="mt-1 text-[#1D3FE8] font-[Bricolage_Grotesque] font-bold text-sm w-6 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <p className="font-[Bricolage_Grotesque] font-bold text-[#1A1A1A] mb-2">
                        {item.label}
                      </p>
                      {item.paragraphs.map((p, j) => (
                        <p
                          key={j}
                          className="text-[#555] text-[0.9375rem] leading-[1.85] mb-2 last:mb-0"
                        >
                          {p}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            {/* Footer nav */}
            <div className="border-t border-[#E2E8F0] py-16 flex items-center justify-between">
              <Link
                to="/#work"
                className="text-sm font-medium text-[#888888] hover:text-[#1A1A1A] transition-colors"
              >
                ← All case studies
              </Link>
              <div className="text-right">
                <p className="text-xs font-medium uppercase tracking-[0.07em] text-[#888888] mb-1">
                  Next case study
                </p>
                <Link
                  to="/case-study/quiz"
                  className="font-[Bricolage_Grotesque] font-extrabold text-lg tracking-[-0.02em] text-[#1A1A1A] hover:text-[#1D3FE8] transition-colors"
                >
                  D2C quiz conversion loop →
                </Link>
              </div>
            </div>
          </div>
          {/* end flex-1 content */}
        </div>
        {/* end flex row */}
      </div>
    </div>
  );
}
